// Checklist: studioaeye-backend/docs/tests-Backend/testingCheckList.md
// Feature: M001-F0213 (web slice)
// Tests: T-CONN-0215, T-CONN-0228, T-CONN-0239 (Δ8: the node's origin is shown to the approver)
// Ticket: #771

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { deviceLinkApi } from '../services/api';
import StepUpDialog from './auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import { getServiceLinkAppMeta } from './serviceLinkAppMeta';
import { describeNodeOrigin } from './serviceLinkOrigin';
import './LandingPage.css';
import './ServiceLinkPage.css';

const normalizeUserCode = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9-]/g, '')
  .slice(0, 16);

/**
 * Operator-facing device-link approval page.
 * GET /Service-Link[?user_code=KPD3-T8H8]
 */
export default function ServiceLinkPage() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCode = useMemo(
    () => normalizeUserCode(searchParams.get('user_code') || searchParams.get('userCode') || ''),
    [searchParams],
  );

  const [userCode, setUserCode] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [approvalContext, setApprovalContext] = useState(null);
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null);

  useEffect(() => {
    setUserCode(initialCode);
    setApprovalContext(null);
    setSelectedOrganisationIds([]);
  }, [initialCode]);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      const redirect = `/Service-Link${initialCode ? `?user_code=${encodeURIComponent(initialCode)}` : ''}`;
      navigate(`/login?redirectTo=${encodeURIComponent(redirect)}`, {
        replace: true,
      });
    }
  }, [loading, isAuthenticated, navigate, initialCode]);

  const loadContext = useCallback(async (code) => {
    setError('');
    setContextLoading(true);
    try {
      const response = await deviceLinkApi.context(code);
      const context = response?.data?.data || response?.data || response;
      const organisations = Array.isArray(context?.organisations) ? context.organisations : [];
      setApprovalContext({ ...context, code, organisations });
      setSelectedOrganisationIds([]);
      if (!organisations.length) {
        // Mantis #1436: name the app from the Spec v2 AppCode registry; the
        // unknown fallback must stay honest, never a guessed service name.
        const appMeta = getServiceLinkAppMeta(context?.app_code);
        setError(`Your StudioAEye account has no organisation permission to link this ${appMeta.name}.`);
      }
      return context;
    } catch (err) {
      const payload = err?.response?.data || {};
      setError(payload.message || err?.message || 'Unable to inspect this device link.');
      return null;
    } finally {
      setContextLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated && initialCode) loadContext(initialCode);
  }, [loading, isAuthenticated, initialCode, loadContext]);

  const approveCode = useCallback(async (code, organisationIds) => {
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      await deviceLinkApi.approve(code, organisationIds);
      setStepUpChallenge(null);
      setPendingApproval(null);
      setDone(true);
      // Never render approval response fields: device credentials are node-only.
      setMessage('Device link approved. Your device will finish linking on its next poll.');
    } catch (err) {
      const payload = err?.response?.data || {};
      const errorCode = payload.error || payload.code || err?.code || '';
      const msg = payload.message || err?.message || 'Unable to approve device link.';

      if (errorCode === 'STEP_UP_REQUIRED') {
        const challenge = getStepUpChallengeFromError(err);
        if (challenge?.id) {
          setPendingApproval({ code, organisationIds });
          setStepUpChallenge(challenge);
        } else {
          setError('Verification could not be started. Refresh the page and retry this device code.');
        }
        return;
      }

      if (errorCode === 'TWO_SV_NOT_ENROLLED') {
        setError('Set up two-step verification in your StudioAEye profile, then retry this device code.');
        return;
      }

      if (errorCode === 'TWO_SV_METHOD_UNAVAILABLE') {
        setError('No supported two-step verification method is available. Restore one in your StudioAEye profile, then retry.');
        return;
      }

      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    const code = normalizeUserCode(userCode);
    setUserCode(code);
    if (!code) {
      setError('Enter the code shown on your device terminal.');
      return;
    }

    if (!approvalContext || approvalContext.code !== code) {
      await loadContext(code);
      return;
    }
    if (!selectedOrganisationIds.length) {
      setError(`Select at least one organisation for this ${getServiceLinkAppMeta(approvalContext.app_code).name}.`);
      return;
    }
    await approveCode(code, selectedOrganisationIds);
  };

  const completeStepUp = async () => {
    const approval = pendingApproval;
    setStepUpChallenge(null);
    setPendingApproval(null);
    if (approval?.code) {
      await approveCode(approval.code, approval.organisationIds);
    }
  };

  const cancelStepUp = () => {
    setStepUpChallenge(null);
    setPendingApproval(null);
    setError('Verification was cancelled. Your device code is still ready to retry.');
  };

  // Mantis #1436: the pending link's app identity. Rendered only once a real
  // device-link context exists — no context, no invented identity.
  const appMeta = approvalContext ? getServiceLinkAppMeta(approvalContext.app_code) : null;

  if (loading || !isAuthenticated) {
    return (
      <div className="service-link-page">
        <div className="service-link-card">
          <p>Checking session…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="service-link-page">
      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify this service link"
        description="Confirm your identity before StudioAEye approves this device link."
        onComplete={completeStepUp}
        onCancel={cancelStepUp}
      />
      <header className="service-link-header">
        <Link to="/" className="service-link-wordmark" aria-label="StudioAEye home">
          🎬 StudioAEye
        </Link>
        <div className="service-link-header-actions">
          <span className="service-link-user">{user?.email || user?.primaryEmail || user?.displayName || 'Signed in'}</span>
          <button type="button" className="btn-ghost" onClick={() => logout?.()}>
            Log out
          </button>
        </div>
      </header>

      <main className="service-link-main">
        <div className="service-link-card">
          <h1>Link service to your StudioAEye account</h1>
          <p className="service-link-subtitle">Enter the code shown in your terminal.</p>

          {done ? (
            <div className="service-link-success" role="status">
              <p>{message}</p>
              <p className="service-link-hint">
                You can close this page. The device finishes linking automatically.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="service-link-form">
              <label htmlFor="user_code" className="service-link-label">
                Device code
              </label>
              <input
                id="user_code"
                name="user_code"
                className="service-link-input"
                value={userCode}
                onChange={(e) => {
                  setUserCode(normalizeUserCode(e.target.value));
                  setApprovalContext(null);
                  setSelectedOrganisationIds([]);
                }}
                placeholder="KPD3-T8H8"
                autoComplete="one-time-code"
                inputMode="text"
                spellCheck={false}
                disabled={submitting}
              />
              <p className="service-link-caveat">
                Only enter this code if you just initiated a sign-in from your device.
              </p>
              {appMeta ? (
                <div
                  className={`service-link-app${appMeta.known ? '' : ' service-link-app--unknown'}`}
                  data-testid="service-link-app"
                >
                  <span className="service-link-app-icon" aria-hidden="true" data-testid="service-link-app-icon">
                    {appMeta.icon}
                  </span>
                  <span className="service-link-app-text">
                    <span className="service-link-app-name" data-testid="service-link-app-name">{appMeta.name}</span>
                    {approvalContext?.install_label ? (
                      <span className="service-link-app-install" data-testid="service-link-app-install">
                        {approvalContext.install_label}
                      </span>
                    ) : null}
                  </span>
                </div>
              ) : null}
              {approvalContext ? (() => {
                // M001-D021 §16.1 Δ8 / T-CONN-0239: Core registers an exact https
                // local_base_url as this service's approved sign-in origin when
                // the link is approved, so the approver sees it first. Plain
                // text only: the value comes from the device, so it is never a link.
                const origin = describeNodeOrigin(approvalContext.local_base_url);
                return (
                  <div className="service-link-origin" data-testid="service-link-origin">
                    <span className="service-link-origin-label">Service address</span>
                    {origin.declared ? (
                      <code className="service-link-origin-value" data-testid="service-link-origin-value">{origin.shown}</code>
                    ) : (
                      <span data-testid="service-link-origin-none">The service did not declare an address.</span>
                    )}
                    <p className="service-link-hint" data-testid="service-link-origin-note">
                      {origin.exactHttps
                        ? 'If this service signs in through StudioAEye, approving registers this exact address as its approved sign-in origin. Approve only if you recognise it.'
                        : 'No sign-in origin will be registered for this link: only an exact https address is accepted.'}
                    </p>
                  </div>
                );
              })() : null}
              {approvalContext?.organisations?.length ? (
                <fieldset className="service-link-organisations">
                  <legend>Select organisations this {appMeta?.name || 'app'} may serve</legend>
                  <p>Access is checked by StudioAEye separately for each user and organisation.</p>
                  {approvalContext.organisations.map((organisation) => {
                    const id = Number(organisation.id);
                    const checked = selectedOrganisationIds.includes(id);
                    return (
                      <label key={organisation.uuid || id}>
                        <input
                          type="checkbox"
                          value={id}
                          data-org-id={organisation.id}
                          checked={checked}
                          onChange={() => setSelectedOrganisationIds((current) => (
                            checked ? current.filter((value) => value !== id) : [...current, id]
                          ))}
                          disabled={submitting}
                        />
                        <span>{organisation.name}</span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}
              {error ? (
                <p className="service-link-error" role="alert">{error}</p>
              ) : null}
              <button type="submit" className="service-link-continue" disabled={submitting || contextLoading}>
                {submitting ? 'Approving…' : contextLoading ? 'Checking permissions…'
                  : approvalContext ? 'Approve selected organisations' : 'Continue'}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
